import { Trans, useLingui } from '@lingui/react/macro';
import { getBlurHashAverageColor } from 'fast-blurhash';
import { Fragment } from 'preact';
import { memo } from 'preact/compat';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import formatDuration from '../utils/format-duration';
import mem from '../utils/mem';
import states from '../utils/states';

import Icon from './icon';
import Link from './link';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent); // https://stackoverflow.com/a/23522755

/*
Media type
===
unknown = unsupported or unrecognized file type
image = Static image
gifv = Looping, soundless animation
video = Video clip
audio = Audio track
*/

const dataAltLabel = 'ALT';
const AltBadge = (props) => {
  const { t } = useLingui();
  const { alt, lang, onToggle, ...rest } = props;
  if (!alt || !alt.trim()) return null;
  return (
    <button
      type="button"
      class="alt-badge clickable"
      {...rest}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onToggle) {
          onToggle();
          return;
        }
        states.showMediaAlt = {
          alt,
          lang,
        };
      }}
      title={t`Media description`}
    >
      {dataAltLabel}
    </button>
  );
};

const MEDIA_CAPTION_LIMIT = 140;
const MEDIA_CAPTION_LIMIT_LONGER = 280;
export const isMediaCaptionLong = mem((caption) =>
  caption?.length
    ? caption.length > MEDIA_CAPTION_LIMIT ||
      /[\n\r].*[\n\r]/.test(caption.trim())
    : false,
);

// https://caniuse.com/http-live-streaming
const isStreamingVideoSupported = (() => {
  try {
    const video = document.createElement('video');
    if (!video.canPlayType) return false;
    return (
      video.canPlayType('application/vnd.apple.mpegurl') !== '' ||
      video.canPlayType('application/x-mpegURL') !== '' ||
      video.canPlayType('audio/mpegurl') !== ''
    );
  } catch (e) {
    return false;
  }
})();

function Media({
  class: className = '',
  media,
  to,
  lang,
  showOriginal,
  autoAnimate,
  showCaption,
  allowLongerCaption,
  checkAspectRatio = true,
  onClick,
  autoplay = false,
  toggleInlineDescription = false,
  onAltToggle,
}) {
  let {
    id,
    blurhash,
    description,
    meta,
    previewRemoteUrl,
    previewUrl,
    remoteUrl,
    url,
    type,
  } = media;
  if (/no\-preview\./i.test(previewUrl)) {
    previewUrl = null;
  }
  const mediaVTN = getSafeViewTransitionName(id || blurhash || url);
  const { original = {}, small, focus } = meta || {};

  const width = showOriginal
    ? original?.width
    : small?.width || original?.width;
  const height = showOriginal
    ? original?.height
    : small?.height || original?.height;
  const mediaURL = showOriginal ? url : previewUrl || url;
  const remoteMediaURL = showOriginal
    ? remoteUrl
    : previewRemoteUrl || remoteUrl;

  const hasPreviewDimensions = small?.width && small?.height;
  const hasDimensions = width && height;
  const orientation = hasDimensions
    ? width > height
      ? 'landscape'
      : 'portrait'
    : null;

  const rgbAverageColor = blurhash ? getBlurHashAverageColor(blurhash) : null;

  const videoRef = useRef();

  let focalPosition;
  if (focus) {
    // Convert focal point to CSS background position
    // Formula from jquery-focuspoint
    // x = -1, y = 1 => 0% 0%
    // x = 0, y = 0 => 50% 50%
    // x = 1, y = -1 => 100% 100%
    const x = ((focus.x + 1) / 2) * 100;
    const y = ((1 - focus.y) / 2) * 100;
    focalPosition = `${x.toFixed(0)}% ${y.toFixed(0)}%`;
  }

  const mediaRef = useRef();

  const [mediaLoadError, setMediaLoadError] = useState(false);

  const Parent = useMemo(
    () =>
      to && !mediaLoadError ? (props) => <Link to={to} {...props} /> : 'div',
    [to, mediaLoadError],
  );

  const remoteMediaURLObj = remoteMediaURL ? getURLObj(remoteMediaURL) : null;
  const isVideoMaybe =
    type === 'unknown' &&
    remoteMediaURLObj &&
    /\.(mp4|m4r|m4v|mov|webm)$/i.test(remoteMediaURLObj.pathname);
  const isStreamingVideoMaybe =
    remoteMediaURLObj &&
    /\.m3u8$/i.test(remoteMediaURLObj.pathname) &&
    isStreamingVideoSupported;
  const isAudioMaybe =
    type === 'unknown' &&
    remoteMediaURLObj &&
    /\.(mp3|ogg|wav|m4a|m4p|m4b)$/i.test(remoteMediaURLObj.pathname);
  const isImage =
    type === 'image' ||
    (type === 'unknown' &&
      previewUrl &&
      !isVideoMaybe &&
      !isStreamingVideoMaybe &&
      !isAudioMaybe);
  const isPreviewVideoMaybe =
    (previewUrl &&
      /\.(mp4|m4r|m4v|mov|webm)$/i.test(getURLObj(previewUrl).pathname)) ||
    isStreamingVideoMaybe;

  const parentRef = useRef();
  const [imageSmallerThanParent, setImageSmallerThanParent] = useState(false);
  useLayoutEffect(() => {
    if (!isImage) return;
    if (!showOriginal) return;
    if (!parentRef.current) return;
    const { offsetWidth, offsetHeight } = parentRef.current;
    const smaller = width < offsetWidth && height < offsetHeight;
    if (smaller) setImageSmallerThanParent(smaller);
  }, [width, height]);

  const maxAspectHeight =
    window.innerHeight * (orientation === 'portrait' ? 0.45 : 0.33);
  const maxHeight = orientation === 'portrait' ? 0 : 160;
  const averageColorStyle = {
    '--average-color': rgbAverageColor && `rgb(${rgbAverageColor.join(',')})`,
  };
  const mediaStyles =
    width && height
      ? {
          '--width': `${width}px`,
          '--height': `${height}px`,
          // Calculate '--aspectWidth' based on aspect ratio calculated from '--width' and '--height', max height has to be 160px
          '--aspectWidth': `${
            (width / height) * Math.max(maxHeight, maxAspectHeight)
          }px`,
          aspectRatio: `${width} / ${height}`,
          ...averageColorStyle,
        }
      : {
          ...averageColorStyle,
        };

  const [inlineDescriptionExpanded, setInlineDescriptionExpanded] =
    useState(false);
  const longDesc = isMediaCaptionLong(description);
  let showInlineDesc = toggleInlineDescription
    ? inlineDescriptionExpanded
    : !!showCaption && !showOriginal && !!description && !longDesc;
  if (
    !toggleInlineDescription &&
    allowLongerCaption &&
    !showInlineDesc &&
    description?.length <= MEDIA_CAPTION_LIMIT_LONGER
  ) {
    showInlineDesc = true;
  }
  const handleAltToggle = onAltToggle
    ? onAltToggle
    : toggleInlineDescription
      ? () => setInlineDescriptionExpanded((expanded) => !expanded)
      : undefined;
  const Figure = !showInlineDesc
    ? Fragment
    : (props) => {
        const { children, ...restProps } = props;
        return (
          <figure {...restProps}>
            {children}
            <figcaption
              class="media-caption"
              lang={lang}
              dir="auto"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (toggleInlineDescription) {
                  setInlineDescriptionExpanded(false);
                  return;
                }
                states.showMediaAlt = {
                  alt: description,
                  lang,
                };
              }}
            >
              {description}
            </figcaption>
          </figure>
        );
      };

  const postViewState = () =>
    window.matchMedia('(min-width: calc(40em + 350px))').matches
      ? 'large'
      : 'small';
  const interceptOnClick = useCallback(
    (e) => {
      const isOnPostPage = e.target.closest('.status-deck');
      if (
        showOriginal ||
        (postViewState() === 'large' && isOnPostPage) ||
        !document.startViewTransition
      ) {
        onClick?.(e);
        return;
      }
      const el =
        e.target.closest('[data-view-transition-name]') ||
        e.target.querySelector('[data-view-transition-name]');
      if (el) {
        // BUG: both link and onClick is triggered at the same time
        // Temporarily disable view transition if has onClick
        // Detecting preventDefault for an onClick has to happen before view transition but it's only possible after click, and this mean the link is already clicked even before we know it's default prevented.
        if (onClick) {
          onClick(e);
        } else {
          e.preventDefault();
          if (el.dataset.viewTransitioned) {
            el.style.viewTransitionName = mediaVTN;
            try {
              document.startViewTransition(() => {
                el.style.viewTransitionName = '';
                location.hash = `#${to}`;
              });
            } catch (e) {
              console.error(e);
              el.style.viewTransitionName = '';
              location.hash = `#${to}`;
            }
          } else {
            location.hash = `#${to}`;
          }
        }
      } else {
        onClick?.(e);
      }
    },
    [mediaVTN, showOriginal, onClick],
  );

  // Prevent media session lingering after unmount
  useEffect(() => {
    return () => {
      const mediaElements =
        parentRef.current?.querySelectorAll?.('video, audio');
      if (mediaElements) {
        mediaElements.forEach((el) => {
          try {
            el.pause();
            el.src = '';
            el.load();
          } catch (e) {}
        });
      }
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
      }
    };
  }, []);

  const [pinchZoomEnabled, setPinchZoomEnabled] = useState(false);
  const onTransform = useCallback((ref, { scale }) => {
    if (ref.instance?.wrapperComponent) {
      const touchAction = scale <= 1.01 ? 'pan-x' : '';
      if (ref.instance.wrapperComponent.style.touchAction !== touchAction) {
        ref.instance.wrapperComponent.style.touchAction = touchAction;
      }
    }
    if (ref.instance?.setup?.panning) {
      ref.instance.setup.panning.disabled = scale <= 1.01;
    }
    if (ref.instance?.setup?.trackPadPanning) {
      ref.instance.setup.trackPadPanning.disabled = scale <= 1.01;
    }
  }, []);
  const onZoom = useCallback((context) => {
    // Prevent synthetic mousedown from cancelling zoom animation
    if (context.instance?.setup?.panning) {
      context.instance.setup.panning.allowLeftClickPan = false;
    }
  }, []);
  const onZoomStop = useCallback((context) => {
    // Restore left-click panning based on zoom level
    if (context.instance?.setup?.panning) {
      context.instance.setup.panning.allowLeftClickPan =
        context.state.scale > 1.01;
    }
  }, []);
  const transformWrapperProps = {
    smooth: false,
    centerZoomedOut: true,
    doubleClick: {
      mode: 'toggle',
      step: 1,
    },
    wheel: {
      wheelDisabled: true,
      step: 0.05,
    },
    velocityAnimation: {
      inertia: 0.9,
    },
    panning: {
      disabled: true,
      allowMiddleClickPan: false,
      allowRightClickPan: false,
    },
    trackPadPanning: {
      disabled: true,
    },
    onTransform,
    onZoom,
    onZoomStop,
  };
  const transformComponentProps = {
    wrapperClass: 'media-zoom',
    wrapperStyle: {
      touchAction: 'pan-x',
      overflow: 'visible',
      // Note: type: unknown might not have width/height
      display: isImage ? 'inherit' : undefined,
    },
  };

  if (isImage) {
    useLayoutEffect(() => {
      if (!isSafari) return;
      if (!showOriginal) return;
      (async () => {
        try {
          await fetch(mediaURL, { mode: 'no-cors' });
          mediaRef.current.src = mediaURL;
        } catch (e) {
          // Ignore
        }
      })();
    }, [mediaURL]);

    return (
      <Figure>
        <Parent
          ref={parentRef}
          class={`media media-image ${className}`}
          onClick={interceptOnClick}
          data-orientation={orientation}
          data-has-alt={!showInlineDesc || undefined}
          style={
            showOriginal
              ? {
                  backgroundImage: `url(${previewUrl})`,
                  '--bg-image': `url(${previewUrl})`,
                  backgroundSize: imageSmallerThanParent
                    ? `${width}px ${height}px`
                    : undefined,
                  ...averageColorStyle,
                }
              : mediaStyles
          }
        >
          {showOriginal ? (
            <TransformWrapper
              {...transformWrapperProps}
              disabled={!pinchZoomEnabled}
            >
              <TransformComponent {...transformComponentProps}>
                <img
                  ref={mediaRef}
                  src={mediaURL}
                  alt={description}
                  width={width}
                  height={height}
                  data-orientation={orientation}
                  loading="eager"
                  decoding="sync"
                  style={{
                    'view-transition-name': mediaVTN,
                  }}
                  onLoad={(e) => {
                    const el = e.target;
                    const mediaImage = el.closest('.media-image');
                    if (mediaImage) {
                      mediaImage.style.backgroundImage = `url(${el.src})`;
                      mediaImage.style.removeProperty('--bg-image');
                    }
                    el.closest('.media-zoom').style.display = '';
                    setPinchZoomEnabled(true);
                  }}
                  onError={(e) => {
                    const { src } = e.target;
                    if (
                      src === mediaURL &&
                      remoteMediaURL &&
                      mediaURL !== remoteMediaURL
                    ) {
                      e.target.src = remoteMediaURL;
                    }
                  }}
                />
              </TransformComponent>
            </TransformWrapper>
          ) : (
            <>
              <img
                src={mediaURL}
                alt={showInlineDesc ? '' : description}
                width={width}
                height={height}
                data-orientation={orientation}
                loading="lazy"
                data-view-transition-name={mediaVTN}
                style={{
                  // backgroundColor:
                  //   rgbAverageColor && `rgb(${rgbAverageColor.join(',')})`,
                  // backgroundPosition: focalBackgroundPosition || 'center',
                  // Duration based on width or height in pixels
                  objectPosition: focalPosition || 'center',
                  // 100px per second (rough estimate)
                  // Clamp between 5s and 120s
                  '--anim-duration': `${Math.min(
                    Math.max(Math.max(width, height) / 100, 5),
                    120,
                  )}s`,
                }}
                onLoad={(e) => {
                  // e.target.closest('.media-image').style.backgroundImage = '';
                  e.target.dataset.loaded = true;
                  const $media = e.target.closest('.media');
                  if (!hasPreviewDimensions && $media) {
                    const { naturalWidth, naturalHeight } = e.target;
                    $media.dataset.orientation =
                      naturalWidth > naturalHeight ? 'landscape' : 'portrait';
                    $media.style.setProperty('--width', `${naturalWidth}px`);
                    $media.style.setProperty('--height', `${naturalHeight}px`);
                    $media.style.aspectRatio = `${naturalWidth}/${naturalHeight}`;
                  }

                  // Check natural aspect ratio vs display aspect ratio
                  if (checkAspectRatio && $media) {
                    const { target } = e;
                    setTimeout(() => {
                      const {
                        clientWidth,
                        clientHeight,
                        naturalWidth,
                        naturalHeight,
                      } = target;
                      if (
                        clientWidth &&
                        clientHeight &&
                        naturalWidth &&
                        naturalHeight
                      ) {
                        const minDimension = 88;
                        if (
                          naturalWidth < minDimension ||
                          naturalHeight < minDimension
                        ) {
                          $media.dataset.hasSmallDimension = true;
                        } else {
                          const displayNaturalHeight =
                            (naturalHeight * clientWidth) / naturalWidth;
                          const almostSimilarHeight =
                            Math.abs(displayNaturalHeight - clientHeight) < 5;

                          if (almostSimilarHeight) {
                            const $mediaParent = $media.closest('.media');
                            if ($mediaParent) {
                              $mediaParent.dataset.hasNaturalAspectRatio = true;
                            }
                          }
                        }
                      }
                    }, 300);
                  }
                }}
                onError={(e) => {
                  const { src } = e.target;
                  if (
                    src === mediaURL &&
                    remoteMediaURL &&
                    mediaURL !== remoteMediaURL
                  ) {
                    e.target.src = remoteMediaURL;
                  } else {
                    setMediaLoadError(true);
                  }
                }}
              />
              {!showInlineDesc && (
                <AltBadge
                  alt={description}
                  lang={lang}
                  onToggle={handleAltToggle}
                />
              )}
            </>
          )}
        </Parent>
        {mediaLoadError && (
          <div>
            <a href={remoteUrl} class="button plain6 small" target="_blank">
              <Icon icon="external" />{' '}
              <span>
                <Trans>Open file</Trans>
              </span>
            </a>
          </div>
        )}
      </Figure>
    );
  } else if (
    type === 'gifv' ||
    type === 'video' ||
    isVideoMaybe ||
    isStreamingVideoMaybe
  ) {
    const hasDuration = original.duration > 0;
    const shortDuration = original.duration < 31;
    const isGIF = type === 'gifv' && shortDuration;
    // If GIF is too long, treat it as a video
    const loopable = original.duration < 61;
    const formattedDuration = formatDuration(original.duration);
    const hoverAnimate = !showOriginal && !autoAnimate && isGIF;
    const autoGIFAnimate = !showOriginal && autoAnimate && isGIF;
    const showProgress = original.duration > 5;

    // This string is only for autoplay + muted to work on Mobile Safari
    const gifHTML = `
      <video
        src="${url}"
        poster="${previewUrl}"
        width="${width}"
        height="${height}"
        data-orientation="${orientation}"
        style="view-transition-name: ${mediaVTN}"
        preload="auto"
        autoplay
        muted
        playsinline
        ${loopable ? 'loop' : ''}
        ondblclick="this.paused ? this.play() : this.pause()"
        ${
          showProgress
            ? "ontimeupdate=\"this.closest('.media-gif') && this.closest('.media-gif').style.setProperty('--progress', `${~~((this.currentTime / this.duration) * 100)}%`)\""
            : ''
        }
      ></video>
  `;

    const videoURL = isStreamingVideoMaybe ? remoteMediaURL : url;
    const videoHTML = `
      <video
        src="${videoURL}"
        poster="${previewUrl}"
        width="${width}"
        height="${height}"
        data-orientation="${orientation}"
        style="view-transition-name: ${mediaVTN}"
        preload="auto"
        ${autoplay ? 'autoplay' : ''}
        playsinline
        ${loopable ? 'loop' : ''}
        controls
      ></video>
    `;

    return (
      <Figure>
        <Parent
          ref={parentRef}
          class={`media ${className} media-${isGIF ? 'gif' : 'video'} ${
            autoGIFAnimate ? 'media-contain' : ''
          } ${hoverAnimate ? 'media-hover-animate' : ''}`}
          data-orientation={orientation}
          data-formatted-duration={
            !showOriginal ? formattedDuration : undefined
          }
          data-label={
            isGIF && !showOriginal && !autoGIFAnimate ? 'GIF' : undefined
          }
          data-has-alt={!showInlineDesc || undefined}
          // style={{
          //   backgroundColor:
          //     rgbAverageColor && `rgb(${rgbAverageColor.join(',')})`,
          // }}
          style={!showOriginal && mediaStyles}
          onClick={(e) => {
            if (hoverAnimate) {
              try {
                videoRef.current.pause();
              } catch (e) {}
            }
            interceptOnClick(e);
          }}
          onMouseEnter={() => {
            if (hoverAnimate) {
              try {
                videoRef.current.play();
              } catch (e) {}
            }
          }}
          onMouseLeave={() => {
            if (hoverAnimate) {
              try {
                videoRef.current.pause();
              } catch (e) {}
            }
          }}
          onFocus={() => {
            if (hoverAnimate) {
              try {
                videoRef.current.play();
              } catch (e) {}
            }
          }}
          onBlur={() => {
            if (hoverAnimate) {
              try {
                videoRef.current.pause();
              } catch (e) {}
            }
          }}
        >
          {showOriginal || autoGIFAnimate ? (
            isGIF && showOriginal ? (
              <TransformWrapper {...transformWrapperProps}>
                <TransformComponent {...transformComponentProps}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: gifHTML,
                    }}
                  />
                </TransformComponent>
              </TransformWrapper>
            ) : isGIF ? (
              <div
                class="video-container"
                dangerouslySetInnerHTML={{
                  __html: gifHTML,
                }}
              />
            ) : (
              <div
                class="video-container"
                dangerouslySetInnerHTML={{ __html: videoHTML }}
              />
            )
          ) : isGIF ? (
            <video
              ref={videoRef}
              src={url}
              poster={previewUrl}
              width={width}
              height={height}
              data-orientation={orientation}
              data-view-transition-name={mediaVTN}
              preload="auto"
              // controls
              playsinline
              loop
              muted
              onTimeUpdate={
                showProgress
                  ? (e) => {
                      const { target } = e;
                      const container = target?.closest('.media-gif');
                      if (container) {
                        const percentage =
                          (target.currentTime / target.duration) * 100;
                        container.style.setProperty(
                          '--progress',
                          `${percentage}%`,
                        );
                      }
                    }
                  : undefined
              }
            />
          ) : (
            <>
              {previewUrl && !isPreviewVideoMaybe ? (
                <img
                  src={previewUrl}
                  alt={showInlineDesc ? '' : description}
                  width={width}
                  height={height}
                  data-orientation={orientation}
                  loading="lazy"
                  decoding="async"
                  data-view-transition-name={mediaVTN}
                  onLoad={(e) => {
                    if (!hasPreviewDimensions) {
                      const $media = e.target.closest('.media');
                      if ($media) {
                        const { naturalHeight, naturalWidth } = e.target;
                        $media.dataset.orientation =
                          naturalWidth > naturalHeight
                            ? 'landscape'
                            : 'portrait';
                        $media.style.setProperty(
                          '--width',
                          `${naturalWidth}px`,
                        );
                        $media.style.setProperty(
                          '--height',
                          `${naturalHeight}px`,
                        );
                        $media.style.aspectRatio = `${naturalWidth}/${naturalHeight}`;
                      }
                    }
                  }}
                />
              ) : (
                <video
                  src={videoURL + '#t=0.1'} // Make Safari show 1st-frame preview
                  width={width}
                  height={height}
                  data-orientation={orientation}
                  data-view-transition-name={mediaVTN}
                  preload="metadata"
                  muted
                  disablePictureInPicture
                  onLoadedMetadata={(e) => {
                    if (!hasDuration) {
                      const { duration } = e.target;
                      if (duration) {
                        const formattedDuration = formatDuration(duration);
                        const container = e.target.closest('.media-video');
                        if (container) {
                          container.dataset.formattedDuration =
                            formattedDuration;
                        }
                      }
                    }
                  }}
                />
              )}
              <div class="media-play">
                <Icon icon="play" size="xl" alt="▶" />
              </div>
            </>
          )}
          {!showOriginal && !showInlineDesc && (
            <AltBadge
              alt={description}
              lang={lang}
              onToggle={handleAltToggle}
            />
          )}
        </Parent>
      </Figure>
    );
  } else if (type === 'audio' || isAudioMaybe) {
    const formattedDuration = formatDuration(original.duration);
    return (
      <Figure>
        <Parent
          class={`media media-audio ${className}`}
          data-formatted-duration={
            !showOriginal ? formattedDuration : undefined
          }
          data-has-alt={!showInlineDesc || undefined}
          onClick={onClick}
          style={!showOriginal && mediaStyles}
        >
          {showOriginal ? (
            previewUrl ? (
              <video
                src={(remoteUrl || url) + '#t=0.1'}
                width={width}
                height={height}
                data-orientation={orientation}
                poster={previewUrl}
                style={{
                  background: `url(${previewUrl}) center/cover`,
                  aspectRatio: `${width}/${height}`,
                }}
                preload="metadata"
                controls
                controlsList="nofullscreen"
                autoPlay={autoplay}
                playsInline
              />
            ) : (
              <audio
                src={remoteUrl || url}
                preload="none"
                controls
                autoPlay={autoplay}
              />
            )
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={showInlineDesc ? '' : description}
              width={width}
              height={height}
              data-orientation={orientation}
              loading="lazy"
              onError={(e) => {
                try {
                  // Remove self if broken
                  e.target?.remove?.();
                } catch (e) {}
              }}
            />
          ) : null}
          {!showOriginal && (
            <>
              <div class="media-play">
                <Icon icon="play" size="xl" alt="▶" />
              </div>
              {!showInlineDesc && (
                <AltBadge
                  alt={description}
                  lang={lang}
                  onToggle={handleAltToggle}
                />
              )}
            </>
          )}
        </Parent>
      </Figure>
    );
  }
}

function getURLObj(url) {
  // Fake base URL if url doesn't have https:// prefix
  return URL.parse(url, location.origin);
}

export function getSafeViewTransitionName(inputString) {
  // Replace any character that is not a letter, number, hyphen, or underscore with a hyphen.
  let safeName = inputString.replace(/[^a-zA-Z0-9_-]/g, '-');

  // Ensure it starts with a letter, underscore, or two hyphens (to prevent starting with a number or single hyphen).
  // This covers edge cases where the original string might start with an invalid character after replacement.
  if (safeName.match(/^[0-9-]/)) {
    safeName = 'vt-' + safeName;
  }

  return safeName;
}

export default memo(Media, (oldProps, newProps) => {
  const oldMedia = oldProps.media || {};
  const newMedia = newProps.media || {};

  return (
    oldMedia?.id === newMedia?.id &&
    oldMedia.url === newMedia.url &&
    oldProps.to === newProps.to &&
    oldProps.class === newProps.class
  );
});
